import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Select, Table } from 'antd';
import { API } from '@/services/api.service';
import { date, errorText } from './format';

/**
 * The accounts seen on imported statements and the project each belongs to.
 * A return counts as „no statement" while its project has no account here
 * whose statements reach the due date.
 */
export default function AccountsPanel({ company, projects, canManage }) {
  const { t } = useTranslation('taxReturns');
  const queryClient = useQueryClient();

  const { data = [], isLoading } = useQuery({
    queryKey: ['tax-returns-accounts', company],
    queryFn: () => API.taxReturns.getBankAccounts(company),
    enabled: Boolean(company),
  });

  const mutation = useMutation({
    mutationFn: ({ account, project }) => API.taxReturns.setBankAccountProject(company, account, project),
    onSuccess: (items) => {
      queryClient.setQueryData(['tax-returns-accounts', company], items);
      queryClient.invalidateQueries({ queryKey: ['tax-returns', company] });
    },
  });

  return (
    <>
      <p className="mb-3 text-sm opacity-70">{t('accounts.help')}</p>
      {mutation.isError && <Alert type="error" showIcon message={errorText(t, mutation.error)} className="mb-3" />}
      <Table
        size="small"
        rowKey="account"
        loading={isLoading}
        dataSource={data}
        pagination={false}
        columns={[
          { title: t('accounts.account'), dataIndex: 'accountFormatted', width: 200 },
          { title: t('accounts.bank'), dataIndex: 'bank', width: 110 },
          { title: t('accounts.currency'), dataIndex: 'currency', width: 80 },
          {
            title: t('fields.project'),
            dataIndex: 'project',
            width: 260,
            render: (p, row) =>
              canManage ? (
                <Select
                  allowClear
                  style={{ width: 240 }}
                  value={p?._id || null}
                  placeholder={t('filters.noProject')}
                  loading={mutation.isPending && mutation.variables?.account === row.account}
                  onChange={(value) => mutation.mutate({ account: row.account, project: value || null })}
                  options={projects.map((x) => ({ value: x._id, label: `${x.code} ${x.name}` }))}
                />
              ) : p ? (
                `${p.code} ${p.name}`
              ) : (
                '—'
              ),
          },
          { title: t('accounts.first'), dataIndex: 'first', render: date, width: 110 },
          { title: t('accounts.latest'), dataIndex: 'latest', render: date, width: 110 },
          { title: t('accounts.statements'), dataIndex: 'statements', align: 'right', width: 90 },
        ]}
      />
    </>
  );
}
